import React, { useEffect } from 'react';

const ContainersVsVMs = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Containers vs VMs - Virtualization Comparison Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Complete comparison of Containers vs Virtual Machines. Learn Docker, hypervisors, resource usage, security, and when to use each virtualization technology.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Complete comparison of Containers vs Virtual Machines. Learn Docker, hypervisors, resource usage, security, and when to use each virtualization technology.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'containers vs virtual machines, Docker, hypervisor, virtualization, containerization, VMs, infrastructure, cloud computing');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'containers vs virtual machines, Docker, hypervisor, virtualization, containerization, VMs, infrastructure, cloud computing';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Containers vs Virtual Machines</h1>
        <p style={styles.subtitle}>
          Complete Comparison of Virtualization Technologies
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Introduction to Virtualization</h2>
          <p style={styles.paragraph}>
            Virtualization allows you to run multiple isolated environments on a single physical 
            machine. The two main approaches are Virtual Machines (VMs) and Containers. While both 
            provide isolation and resource management, they work at different levels of the system 
            stack and have distinct advantages and use cases.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Virtual Machines (VMs)</h2>
          <p style={styles.paragraph}>
            Virtual Machines create complete virtualized hardware environments, each running its 
            own operating system. A hypervisor manages these VMs and allocates physical resources 
            to each virtual environment.
          </p>

          <div style={styles.codeBlock}>
{`# VM Architecture Overview
┌─────────────────────────────────────────────────────────────┐
│                    Physical Hardware                         │
├─────────────────────────────────────────────────────────────┤
│                      Hypervisor                             │
├─────────────────────────────────────────────────────────────┤
│  VM 1           │  VM 2           │  VM 3                   │
│ ┌─────────────┐ │ ┌─────────────┐ │ ┌─────────────┐         │
│ │   App A     │ │ │   App B     │ │ │   App C     │         │
│ ├─────────────┤ │ ├─────────────┤ │ ├─────────────┤         │
│ │Guest OS     │ │ │Guest OS     │ │ │Guest OS     │         │
│ │(Ubuntu)     │ │ │(CentOS)     │ │ │(Windows)    │         │
│ └─────────────┘ │ └─────────────┘ │ └─────────────┘         │
└─────────────────────────────────────────────────────────────┘

# Example: Creating VMs with VirtualBox
# Create a new VM
VBoxManage createvm --name "WebServer" --ostype "Ubuntu_64" --register

# Configure VM resources
VBoxManage modifyvm "WebServer" --memory 2048 --cpus 2 --vram 128

# Create virtual hard disk
VBoxManage createhd --filename "WebServer.vdi" --size 20480

# Attach storage
VBoxManage storagectl "WebServer" --name "SATA Controller" --add sata --controller IntelAHCI
VBoxManage storageattach "WebServer" --storagectl "SATA Controller" --port 0 --device 0 --type hdd --medium "WebServer.vdi"

# Start the VM
VBoxManage startvm "WebServer" --type headless

# Example: VMware vSphere PowerCLI
# Connect to vCenter
Connect-VIServer -Server vcenter.company.com

# Create new VM
New-VM -Name "ProductionApp" -VMHost esxi01.company.com -Datastore datastore1 -DiskGB 50 -MemoryGB 8 -NumCpu 4

# Configure network
Get-VM "ProductionApp" | Get-NetworkAdapter | Set-NetworkAdapter -NetworkName "Production_VLAN"

# Start VM
Start-VM -VM "ProductionApp"

# Example: AWS EC2 Instance (Cloud VM)
aws ec2 run-instances \\
    --image-id ami-0abcdef1234567890 \\
    --count 1 \\
    --instance-type t3.medium \\
    --key-name my-key-pair \\
    --security-group-ids sg-903004f8 \\
    --subnet-id subnet-6e7f829e \\
    --tag-specifications 'ResourceType=instance,Tags=[{Key=Name,Value=WebServer}]'`}
          </div>

          <h3 style={styles.heading}>VM Characteristics</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Complete Isolation:</strong> Each VM has its own OS kernel</li>
            <li style={styles.listItem}><strong>Hardware Virtualization:</strong> Virtualizes CPU, memory, storage, network</li>
            <li style={styles.listItem}><strong>Resource Overhead:</strong> Each VM needs full OS resources</li>
            <li style={styles.listItem}><strong>Boot Time:</strong> Longer startup due to full OS boot process</li>
            <li style={styles.listItem}><strong>Security:</strong> Strong isolation between VMs</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Containers</h2>
          <p style={styles.paragraph}>
            Containers package applications with their dependencies and run on a shared OS kernel. 
            A container runtime manages these containers, providing process-level isolation while 
            sharing the host operating system.
          </p>

          <div style={styles.codeBlock}>
{`# Container Architecture Overview
┌─────────────────────────────────────────────────────────────┐
│                    Physical Hardware                         │
├─────────────────────────────────────────────────────────────┤
│                     Host OS                                 │
├─────────────────────────────────────────────────────────────┤
│                Container Runtime (Docker)                   │
├─────────────────────────────────────────────────────────────┤
│ Container 1     │ Container 2     │ Container 3             │
│ ┌─────────────┐ │ ┌─────────────┐ │ ┌─────────────┐         │
│ │   App A     │ │ │   App B     │ │ │   App C     │         │
│ │Dependencies │ │ │Dependencies │ │ │Dependencies │         │
│ └─────────────┘ │ └─────────────┘ │ └─────────────┘         │
└─────────────────────────────────────────────────────────────┘

# Dockerfile Example - Web Application
FROM node:18-alpine

# Set working directory
WORKDIR /app

# Copy package files
COPY package*.json ./

# Install dependencies
RUN npm ci --only=production

# Copy application code
COPY . .

# Create non-root user
RUN addgroup -g 1001 -S nodejs
RUN adduser -S nextjs -u 1001

# Change ownership
RUN chown -R nextjs:nodejs /app
USER nextjs

# Expose port
EXPOSE 3000

# Health check
HEALTHCHECK --interval=30s --timeout=3s --start-period=5s --retries=3 \\
  CMD curl -f http://localhost:3000/health || exit 1

# Start application
CMD ["npm", "start"]

# Build and run container
docker build -t my-web-app:latest .
docker run -d --name web-app -p 3000:3000 my-web-app:latest

# Docker Compose Example - Multi-service Application
version: '3.8'

services:
  web:
    build: .
    ports:
      - "3000:3000"
    environment:
      - NODE_ENV=production
      - DATABASE_URL=postgresql://user:pass@db:5432/myapp
    depends_on:
      - db
      - redis
    volumes:
      - ./uploads:/app/uploads
    networks:
      - app-network

  db:
    image: postgres:15-alpine
    environment:
      - POSTGRES_DB=myapp
      - POSTGRES_USER=user
      - POSTGRES_PASSWORD=pass
    volumes:
      - postgres_data:/var/lib/postgresql/data
    networks:
      - app-network

  redis:
    image: redis:7-alpine
    command: redis-server --appendonly yes
    volumes:
      - redis_data:/data
    networks:
      - app-network

  nginx:
    image: nginx:alpine
    ports:
      - "80:80"
      - "443:443"
    volumes:
      - ./nginx.conf:/etc/nginx/nginx.conf
      - ./ssl:/etc/nginx/ssl
    depends_on:
      - web
    networks:
      - app-network

volumes:
  postgres_data:
  redis_data:

networks:
  app-network:
    driver: bridge

# Container management commands
docker-compose up -d                    # Start all services
docker-compose ps                       # View running containers
docker-compose logs web                 # View logs for web service
docker-compose exec web sh              # Execute shell in web container
docker-compose down                     # Stop and remove containers

# Advanced container operations
# Multi-stage build for optimization
FROM node:18-alpine AS builder
WORKDIR /app
COPY package*.json ./
RUN npm ci
COPY . .
RUN npm run build

FROM node:18-alpine AS production
WORKDIR /app
COPY --from=builder /app/dist ./dist
COPY --from=builder /app/node_modules ./node_modules
COPY package*.json ./
EXPOSE 3000
CMD ["npm", "start"]

# Container security scanning
docker scan my-web-app:latest

# Resource limits
docker run -d \\
  --name web-app \\
  --memory="512m" \\
  --cpus="1.0" \\
  --restart=unless-stopped \\
  my-web-app:latest`}
          </div>

          <h3 style={styles.heading}>Container Characteristics</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Process Isolation:</strong> Containers share the host OS kernel</li>
            <li style={styles.listItem}><strong>Lightweight:</strong> Minimal resource overhead</li>
            <li style={styles.listItem}><strong>Fast Startup:</strong> Near-instantaneous boot times</li>
            <li style={styles.listItem}><strong>Portability:</strong> "Build once, run anywhere"</li>
            <li style={styles.listItem}><strong>Density:</strong> Can run many more containers than VMs</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Detailed Comparison</h2>
          
          <div style={styles.codeBlock}>
{`| Aspect                | Virtual Machines          | Containers               |
|-----------------------|----------------------------|--------------------------|
| Abstraction Level     | Hardware virtualization   | OS-level virtualization  |
| OS Requirement        | Full OS per VM            | Shared host OS           |
| Resource Usage        | High (GB of RAM per VM)   | Low (MB of RAM)          |
| Startup Time          | Minutes                    | Seconds                  |
| Density               | ~10s per host             | ~100s per host          |
| Isolation             | Strong (hardware level)   | Good (process level)     |
| Security              | Excellent isolation       | Shared kernel risks      |
| Portability           | Platform dependent        | Highly portable          |
| Performance           | Near native               | Near native              |
| Persistence           | Stateful by default       | Stateless by design      |
| Networking            | Virtual network adapters  | Shared network stack     |
| Storage               | Virtual disks             | Layered filesystems      |
| Orchestration         | VMware, Hyper-V          | Kubernetes, Docker Swarm |
| Use Cases             | Legacy apps, different OS | Microservices, CI/CD     |`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Resource Usage Analysis</h2>
          
          <div style={styles.codeBlock}>
{`# Resource Comparison Example

# Virtual Machine Resource Usage
VM Configuration:
- Host: 32 GB RAM, 8 CPU cores
- VM 1: Ubuntu 20.04 (4 GB RAM, 2 cores) - Web Server
- VM 2: CentOS 8 (4 GB RAM, 2 cores) - Database
- VM 3: Windows Server (8 GB RAM, 2 cores) - Application Server
- Hypervisor overhead: ~2 GB RAM

Total RAM usage: 4 + 4 + 8 + 2 = 18 GB (56% utilization)
Available for more VMs: 14 GB (could fit 2-3 more VMs)

# Container Resource Usage
Container Configuration:
- Host: 32 GB RAM, 8 CPU cores
- Container 1: Node.js Web App (512 MB RAM limit)
- Container 2: PostgreSQL Database (1 GB RAM limit)
- Container 3: Redis Cache (256 MB RAM limit)
- Container 4: Nginx Proxy (128 MB RAM limit)
- Container 5: Background Worker (512 MB RAM limit)
- ... (20 more microservices)

Total RAM usage: ~8 GB for 25 containers
Available for more containers: 24 GB (could fit 60+ more containers)

# Performance Benchmarks
# VM Boot Time Test
time VBoxManage startvm "TestVM" --type headless
# Result: ~45-90 seconds

# Container Start Time Test
time docker run --rm hello-world
# Result: ~2-5 seconds

# Memory Usage Monitoring
# VM Memory Usage
free -h  # Inside VM shows allocated memory even if unused

# Container Memory Usage
docker stats --no-stream
# Shows actual memory consumption, not allocated

# Example Output:
CONTAINER ID   NAME        CPU %     MEM USAGE / LIMIT     MEM %
abc123         web-app     0.50%     245.2MiB / 512MiB     47.89%
def456         database    2.10%     756.1MiB / 1GiB       73.84%
ghi789         cache       0.20%     45.8MiB / 256MiB      17.89%`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Security Comparison</h2>
          
          <h3 style={styles.heading}>Virtual Machine Security</h3>
          <div style={styles.codeBlock}>
{`# VM Security Features

# Hypervisor-level isolation
- Each VM has its own kernel space
- Hardware-enforced memory isolation
- Separate virtual network interfaces
- Independent file systems

# VM Security Configuration
# VMware vSphere security settings
Get-VM "ProductionVM" | Get-SecurityPolicy | Set-SecurityPolicy \\
    -EncryptionPolicy "Require" \\
    -LoggingPolicy "Verbose"

# Enable VM encryption
New-VM -Name "SecureVM" -VMHost $vmhost -Datastore $datastore \\
    -StoragePolicy "VM Encryption Policy"

# Network microsegmentation
# Create isolated network segment
New-VDPortgroup -VDSwitch $vds -Name "DMZ_Segment" -VlanId 100

# VM firewall rules (NSX example)
New-NSXFirewallRule \\
    -Name "WebServer_Access" \\
    -Source "Internal_Network" \\
    -Destination "WebServer_VM" \\
    -Service "HTTP,HTTPS" \\
    -Action "Allow"`}
          </div>

          <h3 style={styles.heading}>Container Security</h3>
          <div style={styles.codeBlock}>
{`# Container Security Considerations

# Shared kernel vulnerabilities
- All containers share the host OS kernel
- Kernel exploits can affect all containers
- Container escape possibilities

# Container Security Best Practices

# 1. Use minimal base images
FROM alpine:3.18  # Minimal attack surface
# or
FROM scratch      # Empty base image

# 2. Run as non-root user
RUN adduser -D -s /bin/sh appuser
USER appuser

# 3. Security scanning
docker scout cves my-app:latest
trivy image my-app:latest

# 4. Runtime security
docker run \\
    --read-only \\                    # Read-only filesystem
    --no-new-privileges \\            # Prevent privilege escalation
    --cap-drop=ALL \\                 # Drop all capabilities
    --cap-add=NET_BIND_SERVICE \\     # Add only needed capabilities
    --security-opt=no-new-privileges \\
    my-app:latest

# 5. Network policies (Kubernetes)
apiVersion: networking.k8s.io/v1
kind: NetworkPolicy
metadata:
  name: web-app-policy
spec:
  podSelector:
    matchLabels:
      app: web-app
  policyTypes:
  - Ingress
  - Egress
  ingress:
  - from:
    - podSelector:
        matchLabels:
          app: frontend
    ports:
    - protocol: TCP
      port: 8080

# 6. Secrets management
docker secret create db_password password.txt
docker service create \\
    --name web-app \\
    --secret db_password \\
    my-app:latest

# 7. Container runtime security (gVisor, Kata Containers)
docker run --runtime=gvisor my-app:latest  # User-space kernel
docker run --runtime=kata my-app:latest    # Lightweight VMs`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use Virtual Machines</h2>
          
          <h3 style={styles.heading}>Ideal VM Use Cases</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Different Operating Systems:</strong> Running Windows and Linux workloads</li>
            <li style={styles.listItem}><strong>Legacy Applications:</strong> Monolithic apps that can't be containerized</li>
            <li style={styles.listItem}><strong>Strong Isolation:</strong> Multi-tenant environments with security requirements</li>
            <li style={styles.listItem}><strong>Resource Guarantees:</strong> Applications needing dedicated resources</li>
            <li style={styles.listItem}><strong>Compliance:</strong> Regulatory requirements for isolation</li>
          </ul>

          <div style={styles.codeBlock}>
{`# Example: Enterprise Multi-OS Environment

# VMware vSphere Infrastructure
# Windows Server VMs for .NET applications
New-VM -Name "WebApp-IIS" -VMHost esxi01 -GuestOS "Windows2019" -Memory 8GB -CPU 4
New-VM -Name "Database-MSSQL" -VMHost esxi02 -GuestOS "Windows2019" -Memory 16GB -CPU 8

# Linux VMs for Java applications
New-VM -Name "API-Gateway" -VMHost esxi03 -GuestOS "Ubuntu64" -Memory 4GB -CPU 2
New-VM -Name "Analytics-Spark" -VMHost esxi04 -GuestOS "CentOS7" -Memory 32GB -CPU 16

# Resource allocation and limits
Get-VM "Database-MSSQL" | Set-VM -MemoryGB 16 -NumCpu 8 -CpuReservationMhz 4000

# High availability configuration
New-DrsRule -Name "DB-AntiAffinity" -Cluster $cluster -KeepTogether $false -VM $dbVMs

# Backup and disaster recovery
New-VBRJob -Name "Production-VMs" -BackupObject $productionVMs -Repository $repository

# Example: Development Environment Isolation
# Different OS versions for testing
vagrant init ubuntu/focal64    # Ubuntu 20.04
vagrant init centos/8          # CentOS 8
vagrant init generic/windows10 # Windows 10

# Vagrantfile for multi-VM setup
Vagrant.configure("2") do |config|
  config.vm.define "web" do |web|
    web.vm.box = "ubuntu/focal64"
    web.vm.network "private_network", ip: "192.168.50.10"
    web.vm.provider "virtualbox" do |vb|
      vb.memory = "2048"
      vb.cpus = 2
    end
  end
  
  config.vm.define "db" do |db|
    db.vm.box = "centos/8"
    db.vm.network "private_network", ip: "192.168.50.11"
    db.vm.provider "virtualbox" do |vb|
      vb.memory = "4096"
      vb.cpus = 4
    end
  end
end`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use Containers</h2>
          
          <h3 style={styles.heading}>Ideal Container Use Cases</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Microservices:</strong> Distributed architectures with many small services</li>
            <li style={styles.listItem}><strong>CI/CD Pipelines:</strong> Fast, consistent build and deployment environments</li>
            <li style={styles.listItem}><strong>Cloud-Native Apps:</strong> Applications designed for scalability and portability</li>
            <li style={styles.listItem}><strong>Development Consistency:</strong> "Works on my machine" problem solving</li>
            <li style={styles.listItem}><strong>Auto-scaling:</strong> Dynamic scaling based on demand</li>
          </ul>

          <div style={styles.codeBlock}>
{`# Example: Microservices E-commerce Platform

# Docker Compose for local development
version: '3.8'

services:
  # API Gateway
  gateway:
    image: nginx:alpine
    ports:
      - "80:80"
    volumes:
      - ./nginx.conf:/etc/nginx/nginx.conf
    depends_on:
      - user-service
      - product-service
      - order-service

  # User Service
  user-service:
    build: ./services/user-service
    environment:
      - DATABASE_URL=postgresql://user:pass@user-db:5432/users
      - REDIS_URL=redis://cache:6379
    depends_on:
      - user-db
      - cache

  user-db:
    image: postgres:15-alpine
    environment:
      POSTGRES_DB: users
      POSTGRES_USER: user
      POSTGRES_PASSWORD: pass
    volumes:
      - user_data:/var/lib/postgresql/data

  # Product Service
  product-service:
    build: ./services/product-service
    environment:
      - MONGODB_URL=mongodb://product-db:27017/products
      - ELASTICSEARCH_URL=http://search:9200
    depends_on:
      - product-db
      - search

  product-db:
    image: mongo:6
    volumes:
      - product_data:/data/db

  search:
    image: elasticsearch:8.8.0
    environment:
      - discovery.type=single-node
      - xpack.security.enabled=false
    volumes:
      - search_data:/usr/share/elasticsearch/data

  # Order Service
  order-service:
    build: ./services/order-service
    environment:
      - DATABASE_URL=postgresql://order:pass@order-db:5432/orders
      - RABBITMQ_URL=amqp://queue:5672
    depends_on:
      - order-db
      - queue

  order-db:
    image: postgres:15-alpine
    environment:
      POSTGRES_DB: orders
      POSTGRES_USER: order
      POSTGRES_PASSWORD: pass
    volumes:
      - order_data:/var/lib/postgresql/data

  # Message Queue
  queue:
    image: rabbitmq:3-management-alpine
    ports:
      - "15672:15672"  # Management UI
    volumes:
      - queue_data:/var/lib/rabbitmq

  # Shared Cache
  cache:
    image: redis:7-alpine
    volumes:
      - cache_data:/data

  # Monitoring
  prometheus:
    image: prom/prometheus
    ports:
      - "9090:9090"
    volumes:
      - ./prometheus.yml:/etc/prometheus/prometheus.yml

  grafana:
    image: grafana/grafana
    ports:
      - "3000:3000"
    environment:
      - GF_SECURITY_ADMIN_PASSWORD=admin
    volumes:
      - grafana_data:/var/lib/grafana

volumes:
  user_data:
  product_data:
  search_data:
  order_data:
  queue_data:
  cache_data:
  grafana_data:

# CI/CD Pipeline Example (GitHub Actions)
name: Deploy Microservices

on:
  push:
    branches: [main]

jobs:
  build-and-deploy:
    runs-on: ubuntu-latest
    
    steps:
    - uses: actions/checkout@v3
    
    - name: Build images
      run: |
        docker build -t user-service:\$\{{ github.sha \}} ./services/user-service
        docker build -t product-service:\$\{{ github.sha \}} ./services/product-service
        docker build -t order-service:\$\{{ github.sha \}} ./services/order-service
    
    - name: Push to registry
      run: |
        echo \$\{{ secrets.DOCKER_PASSWORD \}} | docker login -u \$\{{ secrets.DOCKER_USERNAME \}} --password-stdin
        docker push user-service:\$\{{ github.sha \}}
        docker push product-service:\$\{{ github.sha \}}
        docker push order-service:\$\{{ github.sha \}}
    
    - name: Deploy to Kubernetes
      run: |
        kubectl set image deployment/user-service user-service=user-service:\$\{{ github.sha \}}
        kubectl set image deployment/product-service product-service=product-service:\$\{{ github.sha \}}
        kubectl set image deployment/order-service order-service=order-service:\$\{{ github.sha \}}
        kubectl rollout status deployment/user-service
        kubectl rollout status deployment/product-service
        kubectl rollout status deployment/order-service`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Hybrid Approaches</h2>
          <p style={styles.paragraph}>
            Many organizations use both VMs and containers together, leveraging the strengths of 
            each technology. Common hybrid patterns include running containers inside VMs for 
            additional isolation, or using VMs for stateful services and containers for stateless ones.
          </p>

          <div style={styles.codeBlock}>
{`# Example: Hybrid Architecture

# Pattern 1: Containers in VMs
# VM for each environment (dev, staging, prod)
# Containers for applications within each VM

# Production VM with Docker
VM: production-docker-host
- OS: Ubuntu 20.04
- Resources: 32 GB RAM, 8 CPU cores
- Docker Engine: 24.0.x
- Containers: 
  - Web applications (stateless)
  - Background workers
  - Caches (Redis)

# Database VM (dedicated resources)
VM: production-database
- OS: CentOS 8
- Resources: 64 GB RAM, 16 CPU cores
- Applications:
  - PostgreSQL cluster
  - MongoDB replica set
  - Backup services

# Kubernetes on VMs
# Master nodes as VMs
VM: k8s-master-01 (4 GB RAM, 2 CPU)
VM: k8s-master-02 (4 GB RAM, 2 CPU)
VM: k8s-master-03 (4 GB RAM, 2 CPU)

# Worker nodes as VMs
VM: k8s-worker-01 (16 GB RAM, 4 CPU)
VM: k8s-worker-02 (16 GB RAM, 4 CPU)
VM: k8s-worker-03 (16 GB RAM, 4 CPU)

# Applications run as containers on worker VMs
kubectl apply -f - <<EOF
apiVersion: apps/v1
kind: Deployment
metadata:
  name: web-app
spec:
  replicas: 6
  selector:
    matchLabels:
      app: web-app
  template:
    metadata:
      labels:
        app: web-app
    spec:
      containers:
      - name: web-app
        image: my-web-app:latest
        resources:
          requests:
            memory: "512Mi"
            cpu: "250m"
          limits:
            memory: "1Gi"
            cpu: "500m"
EOF

# Pattern 2: VM for stateful, containers for stateless
# Stateful services in VMs (databases, file systems)
# Stateless services in containers (APIs, web servers)

# Terraform configuration for hybrid setup
resource "aws_instance" "database_vm" {
  ami           = "ami-0abcdef1234567890"
  instance_type = "r5.2xlarge"  # Memory optimized for database
  
  tags = {
    Name = "PostgreSQL-Primary"
    Type = "Database"
  }
}

resource "aws_ecs_cluster" "app_cluster" {
  name = "application-cluster"
  
  capacity_providers = ["FARGATE", "FARGATE_SPOT"]
  
  default_capacity_provider_strategy {
    capacity_provider = "FARGATE"
    weight           = 1
  }
}

resource "aws_ecs_service" "web_service" {
  name            = "web-service"
  cluster         = aws_ecs_cluster.app_cluster.id
  task_definition = aws_ecs_task_definition.web_task.arn
  desired_count   = 3
  
  capacity_provider_strategy {
    capacity_provider = "FARGATE"
    weight           = 100
  }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Migration Strategies</h2>
          
          <h3 style={styles.heading}>VM to Container Migration</h3>
          <div style={styles.codeBlock}>
{`# Migration Assessment Checklist

# 1. Application Analysis
- Stateless vs stateful components
- Dependencies and system requirements
- Configuration management
- Data persistence needs
- Network communication patterns

# 2. Containerization Steps

# Step 1: Create Dockerfile
FROM openjdk:11-jre-slim

# Install dependencies
RUN apt-get update && apt-get install -y \\
    curl \\
    netcat \\
    && rm -rf /var/lib/apt/lists/*

# Create application user
RUN groupadd -r appuser && useradd -r -g appuser appuser

# Copy application
COPY target/myapp.jar /app/myapp.jar
RUN chown appuser:appuser /app/myapp.jar

USER appuser

# Health check
HEALTHCHECK --interval=30s --timeout=3s \\
  CMD curl -f http://localhost:8080/health || exit 1

EXPOSE 8080
CMD ["java", "-jar", "/app/myapp.jar"]

# Step 2: Extract configuration
# Move from VM config files to environment variables
# Before (VM): /etc/myapp/config.properties
database.url=jdbc:postgresql://db.internal:5432/myapp
database.user=myapp_user
database.password=secret123

# After (Container): Environment variables
ENV DATABASE_URL=jdbc:postgresql://db:5432/myapp
ENV DATABASE_USER=myapp_user
ENV DATABASE_PASSWORD_FILE=/run/secrets/db_password

# Step 3: Handle persistent data
# Before (VM): Local file system
/var/lib/myapp/uploads/
/var/log/myapp/

# After (Container): Volumes
docker run -v uploads_data:/app/uploads \\
           -v logs_data:/app/logs \\
           myapp:latest

# Step 4: Service discovery
# Before (VM): Static IP addresses or DNS
database.host=192.168.1.100
cache.host=192.168.1.101

# After (Container): Service names
database.host=postgres-service
cache.host=redis-service

# Step 5: Monitoring and logging
# Before (VM): Local log files, system monitoring
tail -f /var/log/myapp/application.log

# After (Container): Centralized logging
docker logs myapp-container
# Or structured logging to stdout/stderr

# Complete migration example
# 1. Build container image
docker build -t myapp:v1.0 .

# 2. Test locally
docker run -d --name myapp-test \\
  -p 8080:8080 \\
  -e DATABASE_URL=jdbc:postgresql://localhost:5432/test \\
  myapp:v1.0

# 3. Deploy to staging
docker-compose -f docker-compose.staging.yml up -d

# 4. Performance testing
ab -n 1000 -c 10 http://staging.myapp.com/api/health

# 5. Production deployment
kubectl apply -f k8s/production/`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Performance Considerations</h2>
          
          <div style={styles.codeBlock}>
{`# Performance Comparison Benchmarks

# CPU Performance
# VM: Near-native performance (95-98%)
# Container: Near-native performance (98-99%)

# Memory Performance
# VM: Overhead from guest OS (2-4 GB per VM)
# Container: Minimal overhead (shared kernel)

# I/O Performance
# VM: Virtual disk overhead, but good with paravirtualization
# Container: Direct access to host filesystem (better performance)

# Network Performance
# VM: Virtual network adapters, NAT overhead
# Container: Shared network stack, bridge networking

# Startup Performance Test
#!/bin/bash

# VM startup time test
echo "Testing VM startup time..."
time VBoxManage startvm "TestVM" --type headless
# Average: 45-90 seconds

# Container startup time test
echo "Testing container startup time..."
time docker run --rm hello-world
# Average: 2-5 seconds

# Resource utilization monitoring
# VM monitoring
vmstat 1 5          # CPU, memory, I/O stats
iostat -x 1 5       # Disk I/O statistics
free -h             # Memory usage

# Container monitoring
docker stats --no-stream                    # Resource usage
docker system df                            # Disk usage
docker system events --filter type=container # Events

# Performance tuning examples

# VM Performance Tuning
# VMware settings
numvcpus = "4"
memsize = "8192"
sched.cpu.min = "2000"           # CPU reservation
sched.mem.min = "4096"           # Memory reservation
tools.syncTime = "TRUE"          # Time synchronization
vmci0.present = "TRUE"           # VMware communication interface

# Container Performance Tuning
# CPU limits and requests
docker run --cpus="1.5" --memory="2g" myapp:latest

# Kubernetes resource management
resources:
  requests:
    memory: "1Gi"
    cpu: "500m"
  limits:
    memory: "2Gi"
    cpu: "1000m"

# Container optimization
# Multi-stage builds for smaller images
FROM node:18-alpine AS builder
WORKDIR /app
COPY package*.json ./
RUN npm ci --only=production

FROM node:18-alpine AS production
WORKDIR /app
COPY --from=builder /app/node_modules ./node_modules
COPY . .
CMD ["npm", "start"]

# Image layer caching
COPY package*.json ./    # Cache dependencies
RUN npm install
COPY . .                 # Copy source code last`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Cost Analysis</h2>
          
          <div style={styles.codeBlock}>
{`# Cost Comparison Example

# Infrastructure Costs (AWS)

# VM-based Architecture (EC2 instances)
# Web tier: 3 × t3.medium ($0.0416/hour × 24 × 30 = $89.86/month)
# App tier: 3 × t3.large ($0.0832/hour × 24 × 30 = $179.71/month)
# DB tier: 1 × r5.xlarge ($0.252/hour × 24 × 30 = $181.44/month)
# Load balancer: 1 × ALB ($16.20/month + data processing)
# Total: ~$467/month (excluding data transfer)

# Container-based Architecture (ECS Fargate)
# Web service: 3 tasks × 0.5 vCPU, 1GB RAM ($0.04048/hour × 24 × 30 = $29.15/month)
# App service: 3 tasks × 1 vCPU, 2GB RAM ($0.08096/hour × 24 × 30 = $58.29/month)
# DB: RDS PostgreSQL t3.medium ($0.068/hour × 24 × 30 = $48.96/month)
# Load balancer: 1 × ALB ($16.20/month)
# Total: ~$152/month (excluding data transfer)

# Savings: ~$315/month (67% cost reduction)

# Resource Utilization Efficiency

# VM Scenario (Over-provisioned)
VM Resources Allocated: 16 vCPUs, 32 GB RAM
Actual Usage: 4 vCPUs (25%), 12 GB RAM (37.5%)
Efficiency: ~30%

# Container Scenario (Right-sized)
Container Resources: 6 vCPUs, 12 GB RAM
Actual Usage: 5.5 vCPUs (92%), 11 GB RAM (92%)
Efficiency: ~92%

# Development Environment Costs

# VM Development Environment
# Per developer: 1 × t3.medium ($30/month)
# 10 developers: $300/month
# Often left running 24/7

# Container Development Environment
# Local development: $0 (Docker Desktop)
# Shared staging: 1 × t3.small ($15/month)
# Total: $15/month

# Operational Costs

# VM Management
- OS patching and updates
- Antivirus and security tools
- Backup and disaster recovery
- Monitoring and alerting
- Manual scaling operations

# Container Management
- Automated deployments
- Built-in health checks
- Auto-scaling capabilities
- Centralized logging
- Infrastructure as code

# TCO (Total Cost of Ownership) Factors
1. Infrastructure costs (compute, storage, network)
2. Licensing costs (OS, middleware, applications)
3. Operational costs (management, monitoring, backup)
4. Development costs (environment setup, CI/CD)
5. Training costs (team skills development)`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Decision Framework</h2>
          
          <h3 style={styles.heading}>Choose Virtual Machines When:</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>You need to run different operating systems</li>
            <li style={styles.listItem}>Strong isolation is required for security/compliance</li>
            <li style={styles.listItem}>Working with legacy monolithic applications</li>
            <li style={styles.listItem}>Applications require dedicated hardware resources</li>
            <li style={styles.listItem}>Long-running, stateful applications</li>
            <li style={styles.listItem}>Disaster recovery and backup requirements</li>
          </ul>

          <h3 style={styles.heading}>Choose Containers When:</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>Building microservices architectures</li>
            <li style={styles.listItem}>Need rapid deployment and scaling</li>
            <li style={styles.listItem}>Want consistent development environments</li>
            <li style={styles.listItem}>Cloud-native applications</li>
            <li style={styles.listItem}>CI/CD pipelines and DevOps practices</li>
            <li style={styles.listItem}>Resource efficiency is important</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            Both VMs and containers are valuable virtualization technologies with distinct advantages. 
            VMs provide strong isolation and are ideal for legacy applications and multi-OS environments. 
            Containers offer lightweight, portable, and efficient deployment for modern cloud-native 
            applications.
          </p>
          
          <p style={styles.paragraph}>
            The choice between VMs and containers—or using both in a hybrid approach—depends on your 
            specific requirements for isolation, performance, scalability, and operational complexity. 
            Many organizations successfully use both technologies to leverage their respective strengths.
          </p>
        </section>
      </main>
    </article>
  );
};

export default ContainersVsVMs;
